import { Injectable } from '@angular/core';
import { environment } from '../../enviroment/enviroment';
import { TokenStorageService } from './token-storage.service';

export interface ImageUploadResponse {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

export interface UploadedImage {
  file: File;
  url: string;
  key: string;
  uploading: boolean;
  uploaded: boolean;
  error?: string;
  preview: string;
}

@Injectable({
  providedIn: 'root'
})
export class S3UploadService {

  // Configuración de S3 (estas deberían venir de variables de entorno)
  private readonly S3_BUCKET = environment.s3BucketImages;
  private readonly S3_REGION = environment.region;
  private readonly API_ENDPOINT = environment.uploadApiUrl; // Endpoint del backend para obtener URLs firmadas
  private readonly UPLOAD_RESERVATION_DOCUMENTS_URL = environment.uploadReservationDocumentsUrl; // Endpoint del backend para guardar documentos de reserva en la base de datos

  constructor(private readonly tokenStorage: TokenStorageService) { }

  private saveReservationDocumentRecordToDB = async (key: string, url: string, reservationId?: string, paymentId?: string,) => {
    try {
      const response = await fetch(`${this.UPLOAD_RESERVATION_DOCUMENTS_URL}/${123}/upload-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.tokenStorage.getAccessToken() || ''}`
        },
        body: JSON.stringify({
          type: 'identidad',
          key,
          url,
          paymentId,
        })
      });
      return response.ok;
    } catch (error) {
      console.error('Error guardando registro de documento en DB:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  /**
   * Genera una vista previa de la imagen
   */
  generatePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      /*// pdf preview
      if (file.type === 'application/pdf') {
        resolve('assets/pdf-icon.png');
        return;
      }*/
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject('Error generando vista previa');
      reader.readAsDataURL(file);
    });
  }

  /**
   * Valida que el archivo sea una imagen válida
   */
  validateImage(file: File): { valid: boolean; error?: string } {
    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Solo se permiten archivos JPG, PNG y WebP' };
    }

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'El archivo no debe superar los 5MB' };
    }

    return { valid: true };
  }

  /**
   * Valida que el archivo sea una imagen válida
   */
  validateFileReservations(file: File): { valid: boolean; error?: string } {
    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Solo se permiten archivos JPG, PNG, WebP y PDF' };
    }

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'El archivo no debe superar los 5MB' };
    }

    return { valid: true };
  }

  /**
   * Genera un nombre único para el archivo
   */
  private generateUniqueFileName(originalName: string, alojamientoId?: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    // Prefijo fijo según guía
    const prefix = 'images/hosting';
    const alojId = alojamientoId ? alojamientoId : 'alojamiento';
    return `${prefix}/${alojId}_${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Genera un nombre único para el archivo de reserva
   */
  private generateUniqueFileNameReservations(originalName: string, reservationId?: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    // Prefijo fijo según guía
    const prefix = 'images/reservations';
    const reservId = reservationId ? reservationId : 'reservation';
    return `${prefix}/${reservId}_${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Sube una imagen a S3 usando URL firmada
   */
 async uploadImageToS3(file: File, alojamientoId?: string): Promise<ImageUploadResponse> {
  try {
    console.log('🚀 Iniciando upload para:', file.name);
    
    // 1. Validar archivo
    const validation = this.validateImage(file);
    if (!validation.valid) {
      console.error('❌ Validación fallida:', validation.error);
      return { success: false, error: validation.error };
    }

    // 2. Generar nombre único
    const fileName = this.generateUniqueFileName(file.name, alojamientoId);
    console.log('📝 Nombre generado:', fileName);

    // 3. Obtener URL firmada del backend
    const requestBody = {
      fileName: fileName,
      fileType: file.type,
      bucket: this.S3_BUCKET
    };
    console.log('📤 Request body:', requestBody);

    const signedUrlResponse = await fetch(`${this.API_ENDPOINT}/signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    console.log('📡 Response status:', signedUrlResponse.status);
    console.log('📡 Response ok:', signedUrlResponse.ok);

    if (!signedUrlResponse.ok) {
      const errorText = await signedUrlResponse.text();
      console.error('❌ Error response:', errorText);
      throw new Error(`Error obteniendo URL firmada: ${signedUrlResponse.status}`);
    }

    const responseData = await signedUrlResponse.json();
    console.log('🔗 Response data completa:', responseData);
    
    const { uploadUrl, publicUrl, key } = responseData;
    console.log('🔗 Upload URL:', uploadUrl);
    console.log('🌐 Public URL:', publicUrl);
    console.log('🔑 Key:', key);

    // 4. Subir archivo a S3
    console.log('⬆️ Subiendo archivo a S3...');
    console.log('📄 File type:', file.type);
    console.log('📏 File size:', file.size);

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      }
    });

    console.log('📡 Upload response status:', uploadResponse.status);
    console.log('📡 Upload response ok:', uploadResponse.ok);

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('❌ Upload error response:', errorText);
      console.error('❌ Upload error headers:', Object.fromEntries(uploadResponse.headers.entries()));
      throw new Error(`Error subiendo archivo a S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    console.log('✅ Upload exitoso!');
    return {
      success: true,
      url: publicUrl,
      key: key
    };

  } catch (error) {
    console.error('💥 Error en uploadImageToS3:', error);
    console.error('💥 Error stack:', error instanceof Error ? error.stack : 'No stack trace');
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido'
    };
  }
}

  /**
   * Sube una imagen a S3 usando URL firmada
   */
  async uploadReservationFileToS3(file: File, reservationId?: string): Promise<ImageUploadResponse> {
    try {
      // 1. Validar archivo
      const validation = this.validateFileReservations(file);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // 2. Generar nombre único
      const fileName = this.generateUniqueFileNameReservations(file.name, reservationId);

      // 3. Obtener URL firmada del backend
      const signedUrlResponse = await fetch(`${this.API_ENDPOINT}/signed-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: fileName,
          fileType: file.type,
          bucket: this.S3_BUCKET
        })
      });
      console.log('🔗 URL firmada obtenida:', signedUrlResponse);
      if (!signedUrlResponse.ok) {
        throw new Error('Error obteniendo URL firmada');
      }

      const { uploadUrl, publicUrl, key } = await signedUrlResponse.json();
      console.log('UPLOAAAAAAAAAAAAAAAAD',uploadUrl);
      // 4. Subir archivo a S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Error subiendo archivo a S3');
      }

      const response = this.saveReservationDocumentRecordToDB(key, publicUrl, reservationId);

      return {
        success: true,
        url: publicUrl,
        key: key // usar el key que devuelve el backend
      };
    } catch (error) {
      console.error('Error en uploadReservationFileToS3:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  /**
   * Lista imágenes en S3 bajo un prefijo
   */
  async listImages(prefix: string = 'images/hosting/'): Promise<{ key: string, url: string }[]> {
    try {
      const response = await fetch(`${this.API_ENDPOINT}/list/${encodeURIComponent(prefix)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        throw new Error('Error listando imágenes');
      }
      return await response.json();
    } catch (error) {
      console.error('Error listando imágenes:', error);
      return [];
    }
  }

  /**
   * Elimina una imagen de S3
   */
  async deleteImageFromS3(imageKey: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.API_ENDPOINT}/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: imageKey,
          bucket: this.S3_BUCKET
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error eliminando imagen:', error);
      return false;
    }
  }

  /**
   * Sube múltiples imágenes
   */
  async uploadMultipleImages(
    files: File[],
    alojamientoId?: string,
    onProgress?: (progress: number, uploadedCount: number) => void
  ): Promise<UploadedImage[]> {
    const results: UploadedImage[] = [];
    let uploadedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        // Generar preview
        const preview = await this.generatePreview(file);

        const uploadedImage: UploadedImage = {
          file,
          url: '',
          key: '',
          uploading: true,
          uploaded: false,
          preview
        };

        results.push(uploadedImage);

        // Subir imagen
        const uploadResult = await this.uploadImageToS3(file, alojamientoId);

        if (uploadResult.success) {
          uploadedImage.url = uploadResult.url!;
          uploadedImage.key = uploadResult.key!;
          uploadedImage.uploaded = true;
          uploadedCount++;
        } else {
          uploadedImage.error = uploadResult.error;
        }

        uploadedImage.uploading = false;

        // Reportar progreso
        if (onProgress) {
          const progress = ((i + 1) / files.length) * 100;
          onProgress(progress, uploadedCount);
        }

      } catch (error) {
        const preview = await this.generatePreview(file).catch(() => '');
        results.push({
          file,
          url: '',
          key: '',
          uploading: false,
          uploaded: false,
          error: error instanceof Error ? error.message : 'Error desconocido',
          preview
        });
      }
    }

    return results;
  }
}